import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";

const PROFILE_LOAD_HINT_MS = 10_000;

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, firebaseUser, loading, authReady } = useAuth();
  const location = useLocation();
  const [showSlowHint, setShowSlowHint] = useState(false);

  useEffect(() => {
    if (authReady && !(firebaseUser && (loading || !user))) {
      setShowSlowHint(false);
      return;
    }
    const t = window.setTimeout(() => setShowSlowHint(true), PROFILE_LOAD_HINT_MS);
    return () => window.clearTimeout(t);
  }, [authReady, firebaseUser, loading, user]);

  if (!authReady || (firebaseUser && (loading || !user))) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-6 px-6"
        style={{
          background: "linear-gradient(160deg, #0d1f1f 0%, #1a2f2f 40%, #0f1a1a 100%)",
        }}
      >
        <BrandLogo />
        {showSlowHint && (
          <div className="max-w-sm text-center space-y-3">
            <p className="text-xs leading-relaxed" style={{ color: "rgba(244,229,195,0.45)" }}>
              Mac で <code className="text-[10px]">npm run dev:api</code> が動いているか、
              <code className="text-[10px]">.env</code> の{" "}
              <code className="text-[10px]">VITE_API_BASE_URL</code>（シミュレータなら{" "}
              <code className="text-[10px]">http://127.0.0.1:3001</code>
              ）を確認してください。API に Firebase Admin が未設定の場合は会員取得ができません。
            </p>
            <button
              type="button"
              className="text-xs px-4 py-2 rounded-lg"
              style={{
                background: "rgba(212,175,55,0.2)",
                color: "#e8d5a3",
                border: "1px solid rgba(212,175,55,0.35)",
              }}
              onClick={() => window.location.reload()}
            >
              再読み込み
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!firebaseUser || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
